import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import './CasinoSection.css';

const weiToUsd = (wei) => {
  if (!wei || wei === '0') return '0.00';
  try { return (Number(BigInt(wei)) / 1e18).toFixed(4); } catch { return '0.00'; }
};

const usdToWei = (usd) => {
  try {
    const val = parseFloat(usd);
    if (isNaN(val) || val <= 0) return null;
    return BigInt(Math.round(val * 1e18)).toString();
  } catch { return null; }
};

/** Calculate potential payout wei given betWei string and payoutMultiplierBps */
const calcPayout = (betWei, payoutMultiplierBps) => {
  try {
    const bet = BigInt(betWei);
    return ((bet * BigInt(payoutMultiplierBps)) / 10000n).toString();
  } catch { return '0'; }
};

// ─── Coin Flip ────────────────────────────────────────────────────────────────

function CoinFlip({ balanceWei, cfg, onRefresh }) {
  const [bet, setBet] = useState('');
  const [choice, setChoice] = useState('heads');
  const [flipping, setFlipping] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [settledFace, setSettledFace] = useState(null); // 'heads' | 'tails'

  const betWei = usdToWei(bet);
  const potentialPayout = cfg && betWei ? calcPayout(betWei, cfg.payoutMultiplierBps) : null;
  const winChancePct = cfg ? ((cfg.winProbabilityBps / 100)).toFixed(0) : null;

  const play = useCallback(async () => {
    setError('');
    if (!betWei) { setError('Enter a valid bet amount'); return; }
    setFlipping(true);
    setResult(null);
    setSettledFace(null);
    try {
      const res = await api.post('/mini-games/coin-flip', { betWei, choice });
      // Stop spinning, settle on result face with a smooth transition
      setFlipping(false);
      setSettledFace(res.data.result);
      setResult(res.data);
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.message || 'Game error');
      setFlipping(false);
    }
  }, [betWei, choice, onRefresh]);

  const coinClass = [
    'coin-visual',
    flipping ? 'coin-flipping' : '',
    !flipping && settledFace === 'tails' ? 'coin-tails' : '',
    !flipping && result ? (result.win ? 'coin-won' : 'coin-lost') : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="game-card">
      <div className="game-card-header">
        <span className="game-icon">🪙</span>
        <h3>Coin Flip</h3>
        {cfg && (
          <p className="game-desc">
            Win chance: {winChancePct}% &nbsp;&middot;&nbsp; Payout: {(cfg.payoutMultiplierBps / 10000).toFixed(2)}&times;
          </p>
        )}
      </div>

      <div className={coinClass}>
        <div className="coin-inner">
          <div className="coin-face coin-face-heads">
            <span className="coin-letter">H</span>
            <span className="coin-ring" />
          </div>
          <div className="coin-face coin-face-tails">
            <span className="coin-letter">T</span>
            <span className="coin-ring" />
          </div>
        </div>
        {!flipping && settledFace && (
          <div className={`coin-result-tag ${result?.win ? 'coin-tag-win' : 'coin-tag-loss'}`}>
            {result?.win ? '✓ WIN' : '✗ LOSS'}
          </div>
        )}
      </div>

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          {result.win
            ? `Landed ${result.result}! You won +$${weiToUsd(result.payoutWei)} USDT`
            : `Landed ${result.result}. Better luck next time!`}
        </div>
      )}

      <div className="game-controls">
        <div className="choice-buttons">
          <button className={`choice-btn ${choice === 'heads' ? 'choice-active' : ''}`} onClick={() => setChoice('heads')} disabled={flipping}>🟡 Heads</button>
          <button className={`choice-btn ${choice === 'tails' ? 'choice-active' : ''}`} onClick={() => setChoice('tails')} disabled={flipping}>⚪ Tails</button>
        </div>
        <div className="bet-row">
          <input className="bet-input" type="number" min="0.01" step="0.01" placeholder="Bet (USDT)" value={bet} onChange={e => setBet(e.target.value)} disabled={flipping} />
          <button className="play-btn" onClick={play} disabled={flipping || !bet}>{flipping ? 'Flipping…' : 'Flip!'}</button>
        </div>
        {potentialPayout && <p className="game-payout-preview">Potential win: <strong>${weiToUsd(potentialPayout)} USDT</strong></p>}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Dice Roll ────────────────────────────────────────────────────────────────

const DIE_PIPS = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 25], [75, 25], [25, 50], [75, 50], [25, 75], [75, 75]],
};

function DieFace({ value, rolling }) {
  const pips = DIE_PIPS[value] || DIE_PIPS[1];
  return (
    <div className={`die-face ${rolling ? 'die-rolling' : ''}`}>
      {pips.map(([x, y], i) => (
        <span key={i} className="die-pip" style={{ left: `${x}%`, top: `${y}%` }} />
      ))}
    </div>
  );
}

function DiceRoll({ balanceWei, cfgOverUnder, cfgSeven, onRefresh }) {
  const [bet, setBet] = useState('');
  const [choice, setChoice] = useState('over');
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [displayDice, setDisplayDice] = useState([1, 1]);
  const rollIntervalRef = useRef(null);
  useEffect(() => () => clearInterval(rollIntervalRef.current), []);

  const betWei = usdToWei(bet);
  const activeCfg = choice === 'seven' ? cfgSeven : cfgOverUnder;
  const potentialPayout = activeCfg && betWei ? calcPayout(betWei, activeCfg.payoutMultiplierBps) : null;

  const play = useCallback(async () => {
    setError('');
    if (!betWei) { setError('Enter a valid bet amount'); return; }
    setRolling(true);
    setResult(null);
    rollIntervalRef.current = setInterval(() => {
      setDisplayDice([Math.ceil(Math.random() * 6), Math.ceil(Math.random() * 6)]);
    }, 80);
    try {
      const res = await api.post('/mini-games/dice-roll', { betWei, choice });
      clearInterval(rollIntervalRef.current);
      setDisplayDice([res.data.die1, res.data.die2]);
      setResult(res.data);
      onRefresh();
    } catch (e) {
      clearInterval(rollIntervalRef.current);
      setError(e.response?.data?.message || 'Game error');
    } finally {
      setRolling(false);
    }
  }, [betWei, choice, onRefresh]);

  const options = [
    { key: 'over',  label: 'Over 7',     cfg: cfgOverUnder },
    { key: 'seven', label: 'Exactly 7',  cfg: cfgSeven },
    { key: 'under', label: 'Under 7',    cfg: cfgOverUnder },
  ];

  return (
    <div className="game-card">
      <div className="game-card-header">
        <span className="game-icon">🎲</span>
        <h3>Dice Roll</h3>
        {activeCfg && (
          <p className="game-desc">
            Win chance: {(activeCfg.winProbabilityBps / 100).toFixed(0)}%
            &nbsp;&middot;&nbsp;
            Payout: {(activeCfg.payoutMultiplierBps / 10000).toFixed(2)}&times;
          </p>
        )}
      </div>

      <div className="dice-visual">
        <DieFace value={displayDice[0]} rolling={rolling} />
        <DieFace value={displayDice[1]} rolling={rolling} />
        {result && <span className="dice-total">= {result.total}</span>}
      </div>

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          {result.win
            ? `You won! +$${weiToUsd(result.payoutWei)} USDT`
            : `Total was ${result.total}. Try again!`}
        </div>
      )}

      <div className="game-controls">
        <div className="choice-buttons">
          {options.map(({ key, label, cfg }) => (
            <button
              key={key}
              className={`choice-btn ${choice === key ? 'choice-active' : ''}`}
              onClick={() => setChoice(key)}
              disabled={rolling}
              title={cfg ? `${(cfg.winProbabilityBps / 100).toFixed(0)}% win · ${(cfg.payoutMultiplierBps / 10000).toFixed(2)}×` : ''}
            >{label}</button>
          ))}
        </div>

        <div className="bet-row">
          <input
            className="bet-input"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="Bet (USDT)"
            value={bet}
            onChange={e => setBet(e.target.value)}
            disabled={rolling}
          />
          <button
            className="play-btn"
            onClick={play}
            disabled={rolling || !bet}
          >
            {rolling ? 'Rolling…' : 'Roll!'}
          </button>
        </div>

        {potentialPayout && (
          <p className="game-payout-preview">
            Potential win: <strong>${weiToUsd(potentialPayout)} USDT</strong>
          </p>
        )}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Spin Wheel ───────────────────────────────────────────────────────────────

const SEGMENT_COLORS = [
  '#2a2a2a', '#2a2a2a', '#2a2a2a',
  '#6b4c11', '#1a4a2e', '#1a3a5c', '#4a1a4a', '#7a4a00',
];

function SpinWheel({ balanceWei, cfgWheel, platformEarnings, onRefresh }) {
  const [bet, setBet] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState('');

  const betWei = usdToWei(bet);
  const segsBps = cfgWheel?.segmentsBps || [];
  const maxMultiplierBps = segsBps.length ? Math.max(...segsBps) : 0;
  const potentialPayout = betWei && maxMultiplierBps
    ? ((BigInt(betWei) * BigInt(maxMultiplierBps)) / 10000n).toString()
    : null;

  const segCount = segsBps.length || 8;
  const segDeg = 360 / segCount;

  const play = useCallback(async () => {
    setError('');
    if (!betWei) { setError('Enter a valid bet amount'); return; }
    setSpinning(true);
    setResult(null);
    try {
      const res = await api.post('/mini-games/spin-wheel', { betWei });
      const { segmentIndex } = res.data;
      const targetDeg = 360 * 5 + (segDeg * segmentIndex + segDeg / 2);
      setRotation(prev => prev + targetDeg);
      setTimeout(() => {
        setResult(res.data);
        setSpinning(false);
        onRefresh();
      }, 2200);
    } catch (e) {
      setError(e.response?.data?.message || 'Game error');
      setSpinning(false);
    }
  }, [betWei, segDeg, onRefresh]);

  const segments = segsBps.length
    ? segsBps.map((bps, i) => ({
        label: bps === 0 ? '0×' : `${(bps / 10000).toFixed(2)}×`,
        color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
      }))
    : Array(8).fill({ label: '?', color: '#333' });

  const gradient = segments.map((seg, i) => {
    const start = i * segDeg;
    const end = start + segDeg;
    return `${seg.color} ${start}deg ${end}deg`;
  }).join(', ');

  return (
    <div className="game-card">
      <div className="game-card-header">
        <span className="game-icon">🎡</span>
        <h3>Spin the Wheel</h3>
        <p className="game-desc">
          {segsBps.length
            ? `${segsBps.filter(b => b > 0).length}/${segsBps.length} winning segments`
            : '8 segments'}
          &nbsp;&middot;&nbsp; Max: {(maxMultiplierBps / 10000).toFixed(2)}&times;
        </p>
      </div>

      <div className="wheel-container">
        <div className="wheel-pointer">&#9660;</div>
        <div
          className="wheel"
          style={{
            background: `conic-gradient(${gradient})`,
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 2s cubic-bezier(0.17, 0.67, 0.12, 1)' : 'none',
          }}
        >
          {segments.map((seg, i) => (
            <span
              key={i}
              className="wheel-label"
              style={{ transform: `rotate(${i * segDeg + segDeg / 2}deg) translateY(-52px) translateX(-50%)` }}
            >
              {seg.label}
            </span>
          ))}
        </div>
      </div>

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          {result.win
            ? `${(result.segmentBps / 10000).toFixed(2)}× — You won! +$${weiToUsd(result.payoutWei)} USDT`
            : `0× — No luck this time!`}
        </div>
      )}

      <div className="game-controls">
        <div className="bet-row">
          <input
            className="bet-input"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="Bet (USDT)"
            value={bet}
            onChange={e => setBet(e.target.value)}
            disabled={spinning}
          />
          <button
            className="play-btn"
            onClick={play}
            disabled={spinning || !bet}
          >
            {spinning ? 'Spinning…' : 'Spin!'}
          </button>
        </div>

        {potentialPayout && (
          <p className="game-payout-preview">
            Max win: <strong>${weiToUsd(potentialPayout)} USDT</strong>
            {platformEarnings && platformEarnings !== '0' && (
              <span className="game-pool-hint"> &nbsp;(Prize pool: ${weiToUsd(platformEarnings)})</span>
            )}
          </p>
        )}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>

        <div className="wheel-legend">
          {[...new Map(segments.map(s => [s.label, s])).values()].map(seg => (
            <span key={seg.label} className="wheel-legend-item">
              <span className="wheel-legend-dot" style={{ background: seg.color }} />
              {seg.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const CasinoSection = ({ vaultBalance, platformEarnings, onBalanceRefresh }) => {
  const [gameConfig, setGameConfig] = useState(null);

  useEffect(() => {
    // /play-vault/config returns a FLAT object (res.data.coinFlip, res.data.spinWheel, ...),
    // not res.data.config. Reading .config left every casino game with no config.
    api.get('/play-vault/config')
      .then(res => setGameConfig(res.data))
      .catch(() => {});
  }, []);

  if (!vaultBalance && vaultBalance !== '0') {
    return (
      <div className="casino-section">
        <p className="casino-connect-msg">Connect your wallet to play casino games.</p>
      </div>
    );
  }

  return (
    <div className="casino-section">
      <div className="casino-games">
        <CoinFlip
          balanceWei={vaultBalance}
          cfg={gameConfig?.coinFlip}
          onRefresh={onBalanceRefresh}
        />
        <DiceRoll
          balanceWei={vaultBalance}
          cfgOverUnder={gameConfig?.diceOverUnder}
          cfgSeven={gameConfig?.diceSeven}
          onRefresh={onBalanceRefresh}
        />
        <SpinWheel
          balanceWei={vaultBalance}
          cfgWheel={gameConfig?.spinWheel}
          platformEarnings={platformEarnings}
          onRefresh={onBalanceRefresh}
        />
      </div>
    </div>
  );
};

export default CasinoSection;
